import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Signed in");
      nav("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[var(--bg)]">
      {/* Left brand pane */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--primary)] text-white relative overflow-hidden">
        <div className="flex items-center gap-3 relative z-10">
          <img
            src="/assets/logo.png"
            alt="Events Management Team"
            className="w-12 h-12 object-cover rounded-full ring-2 ring-white/20 bg-black"
          />
          <div>
            <div className="font-serif text-xl">Events Management Team</div>
            <div className="text-[11px] uppercase tracking-widest opacity-70">Beirut · Lebanon</div>
          </div>
        </div>

        <div className="max-w-md relative z-10">
          <div className="text-xs uppercase tracking-[0.3em] opacity-60 mb-6">A COMPLETE ACCOUNTING CONSOLE</div>
          <h1 className="font-serif text-4xl leading-tight mb-4">
            Every permit, every ticket, every dollar &mdash; ledgered.
          </h1>
          <p className="opacity-80 text-sm leading-relaxed">
            Operational intelligence for event producers. Budget forecasts, live cost ledgers,
            sales analytics and revenue projections in one editorial console.
          </p>
        </div>

        <div className="text-[11px] opacity-60 relative z-10">
          info@eventsmanagement.team &middot; +961 81 998 100
        </div>

        <img
          src="/assets/logo.png"
          alt=""
          aria-hidden
          className="absolute -bottom-24 -right-24 w-[420px] h-[420px] object-cover rounded-full opacity-20 pointer-events-none"
        />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="text-[11px] uppercase tracking-widest text-[var(--text-2)] mb-2">Sign in</div>
          <h2 className="font-serif text-3xl mb-8">Welcome back.</h2>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="rounded-none border-[var(--border-1)] focus-visible:ring-[var(--primary)]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="rounded-none border-[var(--border-1)] focus-visible:ring-[var(--primary)]"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-[var(--accent)] border border-[var(--accent)] bg-[var(--accent)]/5 px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full rounded-none bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white h-11"
            >
              {loading ? "Signing in…" : "Sign in →"}
            </Button>
          </div>

          <div className="mt-8 text-sm text-[var(--text-2)]">
            No account?{" "}
            <Link to="/register" className="text-[var(--primary)] underline underline-offset-4" data-testid="link-register">
              Request access
            </Link>
          </div>
          <div className="rule my-8" />
          <div className="text-[11px] text-[var(--text-2)] leading-relaxed">
            Access is admin-approved. After you request access, an administrator will activate
            your account before you can log in.
          </div>
        </form>
      </div>
    </div>
  );
}
